import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { getSharedChat } from "../api/chats";

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      {isUser ? (
        <div className="max-w-[75%] rounded-2xl rounded-tr-sm bg-accent px-4 py-2.5 text-sm text-white">
          {msg.content}
        </div>
      ) : (
        <div className="max-w-[80%]">
          <div className="prose prose-sm prose-invert max-w-none text-dark-text text-sm leading-relaxed">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
          {msg.sources?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {msg.sources.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-0.5 text-xs text-accent border border-accent/20">
                  📄 {s.file_name} — section {s.chunk_index}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SharedChatPage() {
  const { token } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shared", token],
    queryFn: async () => {
      const res = await getSharedChat(token);
      return res.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-bg">
        <div className="text-dark-muted text-sm">Loading…</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-bg">
        <div className="text-center">
          <p className="text-dark-text font-medium">Chat not found</p>
          <p className="text-dark-muted text-sm mt-1">This shared link may have expired or been disabled.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Header */}
      <div className="border-b border-dark-border bg-dark-surface px-6 py-4">
        <div className="mx-auto max-w-3xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">
              <span className="text-white text-sm font-bold">D</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-dark-text">DocMind</span>
              <span className="mx-2 text-dark-border">·</span>
              <span className="text-sm text-dark-muted">{data.title}</span>
            </div>
          </div>
          <span className="text-xs text-dark-muted rounded-full border border-dark-border px-2.5 py-1">Read-only</span>
        </div>
      </div>

      {/* Messages */}
      <div className="mx-auto max-w-3xl px-6 py-8">
        {data.messages?.map((msg) => (
          <Message key={msg.id} msg={msg} />
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-dark-border py-4 text-center">
        <p className="text-xs text-dark-muted">Powered by DocMind — Private AI Knowledge Platform</p>
      </div>
    </div>
  );
}
